/**
 * agreements — «Договорённости»: обязательства, зафиксированные в разговоре
 * (звонок/встреча/письмо/задача). Секция новая — в старом printReport.ts её
 * не было вовсе. Зеркалит AgreementsSection.tsx: тот же гейт (пустой массив
 * не печатается), тот же приоритет срока (`dueAt` разрешён → локальная дата,
 * иначе `dueHint` — произнесённая в разговоре формулировка), тот же тип через
 * `records.analysisPanel.agreements.type.<type>`.
 *
 * `formatDue` не импортирован из AgreementsSection.tsx — там это приватная,
 * неэкспортируемая функция компонента; переносим эквивалентную логику сюда
 * буквально (не сокращаем и не «улучшаем»), как это уже сделано для printReport
 * с датой в старом коде. Локаль — `i18n.language` из `@/i18n/config`, как в
 * старом printReport.ts.
 */
import i18n from '@/i18n/config';
import { card, tag } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';

/** Разрешённая дата (`dueAt`), если она валидна, иначе произнесённая подсказка (`dueHint`). */
function formatDue(
  dueAt: string | undefined,
  dueHint: string | undefined,
  locale: string
): string | undefined {
  if (dueAt) {
    const ts = new Date(dueAt).getTime();
    if (!Number.isNaN(ts)) {
      return new Date(ts).toLocaleString(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    }
  }
  return dueHint || undefined;
}

export const renderAgreements: SectionFn = (input, t) => {
  const agreements = input.analysis.result.agreements;
  if (!agreements || agreements.length === 0) return '';

  const rows = agreements
    .map((a, idx) => {
      const isLast = idx === agreements.length - 1;
      const due = formatDue(a.dueAt, a.dueHint, i18n.language);
      const confidenceHtml =
        typeof a.confidence === 'number'
          ? ` <span style="font-family:var(--mono);font-size:10px;color:var(--fg3)">${Math.round(a.confidence * 100)}%</span>`
          : '';

      const metaLine =
        due || a.responsibleHint
          ? `<div class="tc" style="font-family:var(--mono);font-size:10px;color:var(--fg3)">${[
              due
                ? `${escapeHtml(t('records.analysisPanel.agreements.due'))}: ${escapeHtml(due)}`
                : '',
              a.responsibleHint
                ? `${escapeHtml(t('records.analysisPanel.agreements.responsible'))}: ${escapeHtml(a.responsibleHint)}`
                : '',
            ]
              .filter(Boolean)
              .join(' · ')}</div>`
          : '';

      return (
        `<div class="box"${isLast ? '' : ' style="margin-bottom:6px"'}>` +
        `<div class="top">${tag(t(`records.analysisPanel.agreements.type.${a.type}`), 'muted')}` +
        `<b>${escapeHtml(a.subject)}</b>${confidenceHtml}</div>` +
        `${a.description ? `<p>${escapeHtml(a.description)}</p>` : ''}` +
        metaLine +
        `${a.evidenceQuote ? `<q>${escapeHtml(a.evidenceQuote)}</q>` : ''}` +
        `</div>`
      );
    })
    .join('');

  return card({
    title: t('records.analysisPanel.agreements.title'),
    value: `<span style="color:var(--fg3)">${agreements.length}</span>`,
    body: rows,
  });
};
